import {
  Package,
  Bike,
  Truck,
  TrainFront,
  Plane,
  Ship,
  Warehouse,
  CheckCircle2,
} from "lucide-react";

/** Line-haul vehicle per transport mode. */
export const MODES = {
  road: Truck,
  rail: TrainFront,
  air: Plane,
  sea: Ship,
};

export const MODE_LABELS = { road: "Road", rail: "Rail", air: "Air", sea: "Sea" };

/**
 * The 8 lifecycle stages, in journey order along the logo "oo" road.
 * `p` = fraction of the journey path length (0 = start of the open road at the
 * top of the left loop, 1 = end of the road at the bottom of the right loop —
 * Delivered is the END of the road, the journey never wraps). Node geometry is
 * computed from these via getPointAtLength — never hard-coded pixels.
 */
export const STAGES = [
  {
    n: 1,
    key: "booked",
    label: "Booked",
    desc: "Shipment created and booking confirmed.",
    icon: Package,
    p: 0.02,
    side: "top",
  },
  {
    n: 2,
    key: "picked-up",
    label: "Picked up",
    desc: "Parcel collected from the sender.",
    icon: Bike,
    p: 0.11,
    side: "top",
  },
  {
    n: 3,
    key: "loaded",
    label: "Loaded to van",
    desc: "Sorted and loaded, heading to the hub.",
    icon: Truck,
    p: 0.25,
    side: "left",
  },
  {
    n: 4,
    key: "line-haul",
    label: "Line-haul departs",
    desc: "Leaving the origin city.",
    icon: null, // mode vehicle
    p: 0.4,
    side: "bottom",
  },
  {
    n: 5,
    key: "in-transit",
    label: "In transit",
    desc: "On the way to the destination city.",
    icon: null, // mode vehicle
    p: 0.55,
    side: "top",
  },
  {
    n: 6,
    key: "arrived",
    label: "Arrived & unloaded",
    desc: "Reached the destination city hub.",
    icon: Warehouse,
    p: 0.73,
    side: "right",
  },
  {
    n: 7,
    key: "out-for-delivery",
    label: "Out for delivery",
    desc: "With the delivery agent, on the final leg.",
    icon: Bike,
    p: 0.88,
    side: "bottom",
  },
  {
    n: 8,
    key: "delivered",
    label: "Delivered",
    desc: "Delivered. Journey complete.",
    icon: CheckCircle2,
    p: 0.98,
    side: "bottom",
  },
];

/** Fraction of the journey path where stage `n` sits. */
export const stageProgressOf = (n) => STAGES[Math.min(Math.max(n, 1), 8) - 1].p;

/** Icon shown on a stage node (stages 4 and 5 depend on the transport mode). */
export const nodeIconFor = (n, mode) => {
  if (n === 4 || n === 5) return MODES[mode] || MODES.road;
  return STAGES[n - 1].icon;
};

/** Vehicle carried on the moving parcel token while heading INTO stage `n`. */
export const tokenVehicleForStage = (n, mode) => {
  if (n === 4 || n === 5) return MODES[mode] || MODES.road;
  return STAGES[n - 1].icon;
};

/** Which journey segment a path fraction belongs to (the stage being approached). */
export const segmentAtProgress = (p) => {
  for (let i = 0; i < STAGES.length; i += 1) {
    if (p <= STAGES[i].p + 1e-4) return STAGES[i].n;
  }
  return 8;
};

/** Highest stage reached at path fraction `p` (for node colouring / captions). */
export const stageAtProgress = (p) => {
  let s = 1;
  for (let i = 0; i < STAGES.length; i += 1) {
    if (STAGES[i].p <= p + 1e-4) s = STAGES[i].n;
  }
  return s;
};
